// @ts-nocheck
import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Code,
  Divider,
  Alert,
  ThemeIcon,
  SimpleGrid,
  Table,
} from '@/components/ui';
import {
  IconInfoCircle,
  IconCloudUpload,
  IconServer,
  IconFiles,
  IconFolders,
} from '@tabler/icons-react';
import {
  // Live-safe (presentational) components
  FileTypeIcon,
  FileEmptyState,
  FileCard,
  FileList,
  FolderCard,
  UploadQueueItem,
  StorageStatCard,
  FileTableRow,
  FolderBreadcrumbNav,
  FileActionsMenu,
  FileFilterPanel,
  DomainStorageChart,
  // Doc-only (SDK / network / full-screen overlay) components
  FileUploader,
  FilePreview,
  UniversalFileViewer,
  BulkActionBar,
} from '@/components/ccms-components/file-management';

/* -------------------------------------------------------------------------- */
/*  Shared mock data — no network, no SDK                                      */
/* -------------------------------------------------------------------------- */

const noop = () => {};

const mockFile = {
  id: 'file-001',
  fileName: 'Q1-Financial-Report.pdf',
  mimeType: 'application/pdf',
  fileSize: 2_457_600,
  status: 'completed',
  uploadedAt: '2024-03-10T10:30:00Z',
  uploadedBy: 'Stanley Masuku',
};

const mockFiles = [
  mockFile,
  {
    id: 'file-002',
    fileName: 'Logo.png',
    mimeType: 'image/png',
    fileSize: 128_400,
    status: 'completed',
    uploadedAt: '2024-02-20T08:15:00Z',
  },
  {
    id: 'file-003',
    fileName: 'Intro.mp4',
    mimeType: 'video/mp4',
    fileSize: 18_900_000,
    status: 'completed',
    uploadedAt: '2024-01-30T16:45:00Z',
  },
];

const mockFolder = {
  id: 'folder-001',
  name: 'Financial Reports',
  path: '/documents/financial',
  fileCount: 42,
  totalSize: 10_485_760,
  color: '#3B82F6',
};

const mockUploadQueueFile = {
  id: 'upload-001',
  fileName: 'annual-budget.xlsx',
  mimeType: 'application/vnd.ms-excel',
  fileSize: 845_000,
  progress: 65,
  status: 'uploading' as const,
};

const mockTableRow = {
  id: 'file-010',
  fileName: 'Marketing-Plan.docx',
  mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  fileSize: 512_000,
  uploadedBy: 'Jane Doe',
  uploadedAt: '2024-04-01T12:00:00Z',
  status: 'uploaded',
};

const mockStorageData = [
  { domain: 'User', fileCount: 100, totalSize: 10_485_760, percentage: 40 },
  { domain: 'Member', fileCount: 75, totalSize: 7_864_320, percentage: 30 },
  { domain: 'Event', fileCount: 50, totalSize: 5_242_880, percentage: 20 },
  { domain: 'Other', fileCount: 25, totalSize: 2_621_440, percentage: 10 },
];

/* -------------------------------------------------------------------------- */
/*  Small helpers for consistent doc cards                                     */
/* -------------------------------------------------------------------------- */

const NEEDS_LIVE = 'CCMS SDK file service / S3 backend (some pieces render fine with mock data)';

function LivePreview({ children }: { children: React.ReactNode }) {
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        Live preview:
      </Text>
      <Card withBorder p="md" radius="md" bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))">
        {children}
      </Card>
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function CcmsFileManagementPage() {
  return (
    <Stack gap="xl">
      {/* Header */}
      <Stack gap="xs">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" radius="md">
            <IconFiles size={22} />
          </ThemeIcon>
          <Title order={2}>CCMS File Management</Title>
        </Group>
        <Text c="dimmed" size="sm" maw={760}>
          Documented stubs for the CCMS file-management component group. These components
          power file upload (S3 via presigned URLs), browsing, previewing, and storage
          analytics. Presentational pieces (cards, rows, badges, charts, breadcrumbs) render
          live here with inline mock data. Components that call the CCMS SDK file service on
          mount, or that render a full-screen / fixed overlay, are documented as code-only so
          this page never crashes or hijacks the viewport.
        </Text>
        <Alert
          variant="light"
          color="blue"
          icon={<IconInfoCircle size={18} />}
          title="What renders live vs. documented-only"
        >
          <Text size="sm">
            Live previews use plain mock objects and no-op handlers — no network, no SDK
            provider required. <b>FileUploader</b> (SDK file service), <b>FilePreview</b> and{' '}
            <b>UniversalFileViewer</b> (full-screen modals) and <b>BulkActionBar</b> (fixed
            floating toolbar) are shown as documentation only.
          </Text>
        </Alert>
      </Stack>

      <Divider
        label={
          <Group gap={6}>
            <IconFiles size={14} />
            <Text size="xs" fw={600} tt="uppercase">
              Live presentational components
            </Text>
          </Group>
        }
        labelPosition="left"
      />

      {/* ------------------------------------------------------------------ */}
      {/* FileTypeIcon                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FileTypeIcon</Title>
            <Text size="sm" c="dimmed">
              Color-coded avatar icon derived from a file's MIME type.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • mimeType — string — required
              <br />• size — 'xs' | 'sm' | 'md' | 'lg' | 'xl' — optional (md)
              <br />• color — string (CSS color) — optional
              <br />• radius — 'xs' | 'sm' | 'md' | 'lg' | 'xl' — optional (sm)
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FileTypeIcon mimeType="image/png" size="md" />
<FileTypeIcon mimeType="application/pdf" size="lg" />
<FileTypeIcon mimeType="video/mp4" color="#FF0000" />`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <Group gap="md">
              <FileTypeIcon mimeType="image/png" size="lg" />
              <FileTypeIcon mimeType="application/pdf" size="lg" />
              <FileTypeIcon mimeType="video/mp4" size="lg" />
              <FileTypeIcon mimeType="audio/mpeg" size="lg" />
              <FileTypeIcon mimeType="application/zip" size="lg" />
            </Group>
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* StorageStatCard                                                     */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>StorageStatCard</Title>
            <Text size="sm" c="dimmed">
              KPI / metric card with gradient accent, optional icon and trend indicator.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • title — string — required
              <br />• value — string | number — required
              <br />• subtitle — string — optional
              <br />• icon — Tabler Icon — optional
              <br />• gradientVariant — 'primary' | 'success' | … — optional
              <br />• trend — 'up' | 'down' | 'neutral' — optional
              <br />• trendValue — string (e.g. "+12%") — optional
            </Text>
          </Stack>

          <Code block language="tsx">
{`<StorageStatCard
  title="Total Files"
  value={1234}
  subtitle="Across all domains"
  icon={IconFiles}
  gradientVariant="primary"
  trend="up"
  trendValue="+12%"
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              <StorageStatCard
                title="Total Files"
                value={1234}
                subtitle="Across all domains"
                icon={IconFiles}
                gradientVariant="primary"
                trend="up"
                trendValue="+12%"
              />
              <StorageStatCard
                title="Storage Used"
                value="24.6 GB"
                subtitle="of 100 GB"
                icon={IconServer}
                gradientVariant="info"
                trend="up"
                trendValue="+3.1 GB"
              />
              <StorageStatCard
                title="Folders"
                value={86}
                subtitle="Organised libraries"
                icon={IconFolders}
                gradientVariant="success"
                trend="neutral"
                trendValue="0%"
              />
            </SimpleGrid>
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FileCard                                                            */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FileCard</Title>
            <Text size="sm" c="dimmed">
              Single-file card with type icon, size, status badge and action buttons.
              Supports a compact and detailed variant.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • file — FileInfo (id, fileName, mimeType, fileSize, …) — required
              <br />• onDownload / onDelete / onPreview — (fileId) =&gt; void — optional
              <br />• showActions — boolean — optional (true)
              <br />• variant — 'compact' | 'detailed' — optional (detailed)
              <br />• gradientVariant — GradientVariant — optional
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FileCard
  file={{
    id: 'file-123',
    fileName: 'document.pdf',
    mimeType: 'application/pdf',
    fileSize: 1024000,
    uploadedAt: '2024-01-15T10:30:00Z',
  }}
  onDownload={(id) => handleDownload(id)}
  onDelete={(id) => handleDelete(id)}
  showActions
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <Stack gap="md">
              <FileCard
                file={mockFile}
                variant="detailed"
                onDownload={noop}
                onDelete={noop}
                onPreview={noop}
              />
              <FileCard file={mockFiles[1]} variant="compact" onDownload={noop} />
            </Stack>
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FileList                                                            */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FileList</Title>
            <Text size="sm" c="dimmed">
              Renders an array of files as a stacked list or responsive grid of FileCards,
              with built-in loading and empty states.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • files — FileInfo[] — required
              <br />• layout — 'list' | 'grid' — optional (list)
              <br />• onDownload / onDelete / onPreview — (fileId) =&gt; void — optional
              <br />• emptyMessage — string — optional
              <br />• loading — boolean — optional
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FileList
  files={files}
  layout="grid"
  onDownload={handleDownload}
  onDelete={handleDelete}
  onPreview={handlePreview}
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <FileList files={mockFiles} layout="grid" onDownload={noop} onPreview={noop} />
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FolderCard                                                          */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FolderCard</Title>
            <Text size="sm" c="dimmed">
              Grid-view folder tile with colored accent, file count, total size and
              rename/delete actions.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • folder — FolderInfo (id, name, path, fileCount?, totalSize?, color?) — required
              <br />• onClick — (folderId) =&gt; void — optional
              <br />• onRename / onDelete — (folderId) =&gt; void — optional
              <br />• variant — 'default' | 'elevated' | 'outlined' — optional
              <br />• hoverEffect — 'glow' | 'lift' | 'scale' | 'border' — optional
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FolderCard
  folder={{
    id: 'folder-123',
    name: 'Documents',
    path: '/documents',
    fileCount: 42,
    totalSize: 10485760,
    color: '#3B82F6',
  }}
  onClick={(id) => navigateToFolder(id)}
  onRename={(id) => handleRename(id)}
  onDelete={(id) => handleDelete(id)}
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <FolderCard folder={mockFolder} onClick={noop} onRename={noop} onDelete={noop} />
              <FolderCard
                folder={{
                  id: 'folder-002',
                  name: 'Marketing Assets',
                  path: '/documents/marketing',
                  fileCount: 18,
                  totalSize: 5_242_880,
                  color: '#7C3AED',
                }}
              />
            </SimpleGrid>
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* UploadQueueItem                                                     */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>UploadQueueItem</Title>
            <Text size="sm" c="dimmed">
              A single row in an upload queue: file icon, status badge, progress bar and
              cancel/retry actions. Purely driven by the file prop's status/progress.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • file — UploadQueueFile (id, fileName, mimeType, fileSize, progress, status) —
              required
              <br />• onCancel — (fileId) =&gt; void — optional
              <br />• onRetry — (fileId) =&gt; void — optional
              <br />• showIcon — boolean — optional (true)
            </Text>
          </Stack>

          <Code block language="tsx">
{`<UploadQueueItem
  file={{
    id: 'upload-123',
    fileName: 'document.pdf',
    mimeType: 'application/pdf',
    fileSize: 1024000,
    progress: 65,
    status: 'uploading',
  }}
  onCancel={(id) => cancelUpload(id)}
  onRetry={(id) => retryUpload(id)}
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <Stack gap="sm">
              <UploadQueueItem file={mockUploadQueueFile} onCancel={noop} />
              <UploadQueueItem
                file={{
                  id: 'upload-002',
                  fileName: 'photo.jpg',
                  mimeType: 'image/jpeg',
                  fileSize: 2_048_000,
                  progress: 100,
                  status: 'completed',
                }}
              />
              <UploadQueueItem
                file={{
                  id: 'upload-003',
                  fileName: 'broken.zip',
                  mimeType: 'application/zip',
                  fileSize: 9_999_999,
                  progress: 40,
                  status: 'failed',
                  error: 'Network error — please retry',
                }}
                onCancel={noop}
                onRetry={noop}
              />
            </Stack>
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FileTableRow                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FileTableRow</Title>
            <Text size="sm" c="dimmed">
              A reusable <Code>&lt;tr&gt;</Code> for file tables — checkbox selection, type
              icon, size, uploader, date, status badge and row actions. Must be rendered
              inside a table body.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • file — FileTableRowData (id, fileName, mimeType, fileSize, …) — required
              <br />• selected — boolean — optional
              <br />• onSelect — (fileId, selected) =&gt; void — optional
              <br />• onPreview / onDownload / onDelete / onMove — (fileId) =&gt; void — optional
              <br />• showActions — boolean — optional (true)
            </Text>
          </Stack>

          <Code block language="tsx">
{`<table>
  <tbody>
    <FileTableRow
      file={{
        id: 'file-123',
        fileName: 'document.pdf',
        mimeType: 'application/pdf',
        fileSize: 1024000,
        uploadedBy: 'John Doe',
        uploadedAt: '2024-01-15T10:30:00Z',
        status: 'uploaded',
      }}
      onSelect={(id, sel) => handleSelect(id, sel)}
      onPreview={(id) => handlePreview(id)}
    />
  </tbody>
</table>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <Table>
              <Table.Tbody>
                <FileTableRow
                  file={mockTableRow}
                  selected={false}
                  onSelect={noop}
                  onPreview={noop}
                  onDownload={noop}
                  onDelete={noop}
                />
                <FileTableRow
                  file={{
                    id: 'file-011',
                    fileName: 'Quarterly.pdf',
                    mimeType: 'application/pdf',
                    fileSize: 1_200_000,
                    uploadedBy: 'Sam Lee',
                    uploadedAt: '2024-03-22T09:00:00Z',
                    status: 'pending',
                  }}
                  selected
                  onSelect={noop}
                  onPreview={noop}
                  onDownload={noop}
                  onDelete={noop}
                />
              </Table.Tbody>
            </Table>
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FolderBreadcrumbNav                                                 */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FolderBreadcrumbNav</Title>
            <Text size="sm" c="dimmed">
              Breadcrumb navigation parsed from a folder path string, with an optional home
              icon. Clicking a crumb fires onNavigate with that crumb's path.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • path — string (e.g. "/documents/2024") — required
              <br />• onNavigate — (path) =&gt; void — required
              <br />• maxItems — number — optional
              <br />• showHome — boolean — optional (true)
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FolderBreadcrumbNav
  path="/documents/2024/reports"
  onNavigate={(path) => navigateToFolder(path)}
  showHome
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <FolderBreadcrumbNav
              path="/documents/2024/reports"
              onNavigate={noop}
              showHome
            />
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FileActionsMenu                                                     */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FileActionsMenu</Title>
            <Text size="sm" c="dimmed">
              A "kebab" (⋮) action menu for a file. Each menu item only renders when its
              handler is provided. Click the trigger in the preview to open the dropdown.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • fileId — string — required
              <br />• onPreview / onDownload / onDelete / onMove / onCopyUrl / onShare —
              (fileId) =&gt; void — optional
              <br />• position — 'bottom' | 'left' | 'right' | 'top' — optional (bottom)
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FileActionsMenu
  fileId="file-123"
  onPreview={(id) => handlePreview(id)}
  onDownload={(id) => handleDownload(id)}
  onDelete={(id) => handleDelete(id)}
  onMove={(id) => handleMove(id)}
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <Group>
              <Text size="sm" c="dimmed">
                Open actions
              </Text>
              <FileActionsMenu
                fileId="file-123"
                onPreview={noop}
                onDownload={noop}
                onCopyUrl={noop}
                onShare={noop}
                onMove={noop}
                onDelete={noop}
              />
            </Group>
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FileFilterPanel                                                     */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FileFilterPanel</Title>
            <Text size="sm" c="dimmed">
              Collapsible advanced filter panel (domain / file type / status multi-selects)
              with a "Clear All" affordance. Controlled via the filters prop.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • filters — FileFilters (domains?, fileTypes?, status?) — required
              <br />• onFilterChange — (filters) =&gt; void — required
              <br />• availableDomains — string[] — optional
              <br />• availableTypes — string[] — optional
              <br />• opened — boolean — optional (false)
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FileFilterPanel
  filters={{ domains: ['User'], fileTypes: ['image'] }}
  onFilterChange={(filters) => applyFilters(filters)}
  availableDomains={['User', 'Member', 'Event']}
  availableTypes={['image', 'video', 'document']}
  opened
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <FileFilterPanel
              filters={{ domains: ['User'], fileTypes: ['image'] }}
              onFilterChange={noop}
              availableDomains={['User', 'Member', 'Event']}
              availableTypes={['image', 'video', 'document']}
              opened
            />
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* DomainStorageChart                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>DomainStorageChart</Title>
            <Text size="sm" c="dimmed">
              Visualises storage usage per domain as a ring (with legend) or horizontal bar
              chart. Fully driven by the data prop.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • data — DomainStorageData[] (domain, fileCount, totalSize, percentage) —
              required
              <br />• chartType — 'ring' | 'bar' — optional (ring)
              <br />• colorScheme — string[] — optional
              <br />• showLegend — boolean — optional (true)
            </Text>
          </Stack>

          <Code block language="tsx">
{`<DomainStorageChart
  data={[
    { domain: 'User', fileCount: 100, totalSize: 10485760, percentage: 40 },
    { domain: 'Member', fileCount: 75, totalSize: 7864320, percentage: 30 },
  ]}
  chartType="ring"
  showLegend
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <DomainStorageChart data={mockStorageData} chartType="ring" showLegend />
              <DomainStorageChart data={mockStorageData} chartType="bar" />
            </SimpleGrid>
          </LivePreview>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FileEmptyState                                                      */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FileEmptyState</Title>
            <Text size="sm" c="dimmed">
              Centered empty-state for file/folder lists, with an optional call-to-action
              button.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • message — string — optional ('No files found')
              <br />• icon — Tabler Icon — optional
              <br />• action — string (button label) — optional
              <br />• onActionClick — () =&gt; void — optional
              <br />• gradientVariant — GradientVariant — optional
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FileEmptyState
  message="No files found"
  action="Upload Files"
  onActionClick={() => openUploadDialog()}
/>`}
          </Code>

          <Badge variant="light" color="green" size="sm">
            Renders live with mock data
          </Badge>

          <LivePreview>
            <FileEmptyState
              message="No files in this folder yet"
              action="Upload Files"
              onActionClick={noop}
            />
          </LivePreview>
        </Stack>
      </Card>

      <Divider
        label={
          <Group gap={6}>
            <IconServer size={14} />
            <Text size="xs" fw={600} tt="uppercase">
              SDK / overlay components — documentation only
            </Text>
          </Group>
        }
        labelPosition="left"
      />

      {/* ------------------------------------------------------------------ */}
      {/* FileUploader (doc only)                                             */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Title order={4}>FileUploader</Title>
              <Text size="sm" c="dimmed">
                Drag-and-drop / button / inline uploader with progress tracking. Internally
                calls <Code>useFileUpload</Code> → <Code>useFileService()</Code> from the CCMS
                SDK on mount, uploading to S3 via presigned URLs.
              </Text>
            </Stack>
            <ThemeIcon variant="light" color="orange" size="lg" radius="md">
              <IconCloudUpload size={20} />
            </ThemeIcon>
          </Group>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • domain — string — required
              <br />• recordId — string — optional
              <br />• multiple — boolean — optional (false)
              <br />• maxSize — number (bytes) — optional (100MB)
              <br />• accept — string[] (mime globs) — optional (['*/*'])
              <br />• variant — 'dropzone' | 'button' | 'inline' — optional (dropzone)
              <br />• onUploadComplete / onUploadError / onProgress — callbacks — optional
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FileUploader
  domain="User"
  recordId="user-123"
  multiple
  maxSize={10 * 1024 * 1024} // 10MB
  accept={['image/*', 'application/pdf']}
  onUploadComplete={(files) => console.log('Uploaded:', files)}
/>`}
          </Code>

          <Alert
            variant="light"
            color="orange"
            icon={<IconServer size={18} />}
            title="Needs a live backend"
          >
            <Text size="sm">{NEEDS_LIVE}</Text>
            <Text size="xs" c="dimmed" mt={4}>
              Documented only — calls the CCMS SDK file service on mount and would throw
              without a configured SDK provider, so it is not rendered live here.
            </Text>
          </Alert>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* FilePreview (doc only)                                              */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>FilePreview</Title>
            <Text size="sm" c="dimmed">
              Modal preview for a single file (image / video / audio / PDF inline, otherwise a
              download fallback) plus a metadata panel. The modal is always open while
              mounted, so it is documented rather than rendered inline.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • file — FileInfo (with url for inline preview) — required
              <br />• onClose — () =&gt; void — optional
              <br />• showDownload — boolean — optional (true)
              <br />• showMetadata — boolean — optional (true)
            </Text>
          </Stack>

          <Code block language="tsx">
{`<FilePreview
  file={file}
  onClose={() => setPreviewOpen(false)}
  showDownload
  showMetadata
/>`}
          </Code>

          <Alert
            variant="light"
            color="orange"
            icon={<IconServer size={18} />}
            title="Overlay component"
          >
            <Text size="sm">{NEEDS_LIVE}</Text>
            <Text size="xs" c="dimmed" mt={4}>
              Documented only — it renders an always-open modal and loads file content from a
              url, so rendering it inline would hijack the page.
            </Text>
          </Alert>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* UniversalFileViewer (doc only)                                     */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>UniversalFileViewer</Title>
            <Text size="sm" c="dimmed">
              Full-screen lightbox viewer supporting images, video, YouTube/Vimeo embeds,
              audio, PDFs, text/code, documents and archives, with zoom/rotate, keyboard
              shortcuts and a thumbnail gallery. Renders a full-viewport modal and fetches
              file blobs for downloads.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • files — ViewerFile[] — required
              <br />• opened — boolean — required
              <br />• onClose — () =&gt; void — required
              <br />• initialIndex — number — optional (0)
              <br />• onDownload / onIndexChange — callbacks — optional
              <br />• showGallery / showToolbar / enableKeyboard — boolean — optional
            </Text>
          </Stack>

          <Code block language="tsx">
{`<UniversalFileViewer
  opened={isOpen}
  onClose={() => setIsOpen(false)}
  files={fileArray}
  initialIndex={0}
/>`}
          </Code>

          <Alert
            variant="light"
            color="orange"
            icon={<IconServer size={18} />}
            title="Full-screen overlay"
          >
            <Text size="sm">{NEEDS_LIVE}</Text>
            <Text size="xs" c="dimmed" mt={4}>
              Documented only — opening it renders a full-viewport modal and it fetches file
              URLs on download, so it is not rendered inline here.
            </Text>
          </Alert>
        </Stack>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* BulkActionBar (doc only)                                           */}
      {/* ------------------------------------------------------------------ */}
      <Card withBorder p="lg" radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={4}>BulkActionBar</Title>
            <Text size="sm" c="dimmed">
              Floating toolbar that appears when files are selected, offering download / move /
              delete and a clear-selection action. It is fixed to the bottom-center of the
              viewport, so it is documented rather than rendered inline.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • selectedCount — number — required (renders nothing when 0)
              <br />• onClear — () =&gt; void — required
              <br />• onDelete / onMove / onDownload — () =&gt; void — optional
              <br />• gradientVariant — GradientVariant — optional
            </Text>
          </Stack>

          <Code block language="tsx">
{`<BulkActionBar
  selectedCount={5}
  onDelete={() => handleBulkDelete()}
  onMove={() => handleBulkMove()}
  onDownload={() => handleBulkDownload()}
  onClear={() => clearSelection()}
/>`}
          </Code>

          <Alert
            variant="light"
            color="orange"
            icon={<IconServer size={18} />}
            title="Fixed floating overlay"
          >
            <Text size="sm">{NEEDS_LIVE}</Text>
            <Text size="xs" c="dimmed" mt={4}>
              Documented only — it renders with <Code>position: fixed</Code> and would float
              over the entire page, so it is not rendered inline here.
            </Text>
          </Alert>
        </Stack>
      </Card>
    </Stack>
  );
}
